
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useMemo, useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, ThumbsUpIcon, ThumbsDownIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import { trackEvent } from '../services/analyticsService';
import { Skeleton } from './ui/Skeleton';

interface OutfitVariationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  variations: string | null;
  isLoading: boolean;
  error: string | null;
  onRegenerate: () => void;
}

const OutfitVariationsModal: React.FC<OutfitVariationsModalProps> = ({ isOpen, onClose, variations, isLoading, error, onRegenerate }) => {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);

  useEffect(() => {
    if (isOpen) {
        setFeedback(null);
    }
  }, [isOpen, variations]);

  const parsedVariations = useMemo(() => {
    if (!variations) return [];
    // Split by markdown heading '### ' and filter out any empty strings resulting from the split.
    return variations.split('### ').filter(s => s.trim()).map(section => {
        const lines = section.split('\n').filter(l => l.trim());
        const title = lines[0] || '';
        const items = lines.slice(1).map(l => l.replace(/^[*-]\s*/, '').trim()).filter(Boolean);
        return { title, items };
    });
  }, [variations]);

  const handleFeedback = (type: 'up' | 'down') => {
      setFeedback(type);
      trackEvent('outfit_variations_feedback', { rating: type });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-serif tracking-wider text-gray-800 dark:text-gray-100">{t('variationsModal_title')}</h2>
              <button
                onClick={onClose}
                className="p-1 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                aria-label={t('variationsModal_close')}
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto custom-scrollbar flex-grow flex flex-col gap-6">
              {isLoading ? (
                <div className="space-y-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border dark:border-gray-700">
                            <Skeleton className="h-6 w-1/3 mb-4" />
                            <div className="space-y-2">
                                <Skeleton className="h-4 w-3/4" />
                                <Skeleton className="h-4 w-5/6" />
                                <Skeleton className="h-4 w-2/3" />
                            </div>
                        </div>
                    ))}
                    <p className="text-center text-sm text-gray-500 animate-pulse">{t('variationsModal_loading')}</p>
                </div>
              ) : error ? (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md w-full dark:bg-red-900/20 dark:border-red-600 dark:text-red-300" role="alert">
                  <p className="font-bold">{t('error_title')}</p>
                  <p>{error}</p>
                </div>
              ) : parsedVariations.length > 0 ? (
                <>
                    <div className="space-y-6">
                        {parsedVariations.map((variation, index) => (
                            <motion.div 
                                key={index}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border dark:border-gray-700"
                            >
                                <h3 className="text-lg font-semibold font-serif text-gray-800 dark:text-gray-100">{variation.title}</h3>
                                <ul className="mt-2 list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                                    {variation.items.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                    <div className="flex items-center justify-center gap-4 pt-4 border-t border-gray-100 dark:border-gray-700/50 w-full">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Was this helpful?</span>
                        <button 
                            onClick={() => handleFeedback('up')} 
                            className={`p-1.5 rounded-full transition-colors ${feedback === 'up' ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400'}`}
                        >
                            <ThumbsUpIcon className="w-5 h-5" />
                        </button>
                        <button 
                            onClick={() => handleFeedback('down')} 
                            className={`p-1.5 rounded-full transition-colors ${feedback === 'down' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400'}`}
                        >
                            <ThumbsDownIcon className="w-5 h-5" />
                        </button>
                        {feedback && <span className="text-xs text-indigo-600 dark:text-indigo-400 animate-fade-in">Thanks!</span>}
                    </div>
                </>
              ) : null}
            </div>
             <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button
                    onClick={onRegenerate}
                    disabled={isLoading}
                    className="flex-shrink-0 bg-gray-900 text-white font-semibold py-2 px-5 rounded-lg transition-colors duration-200 ease-in-out hover:bg-gray-700 active:scale-95 text-base disabled:opacity-50 disabled:cursor-not-allowed dark:bg-indigo-600 dark:hover:bg-indigo-500"
                >
                    {t('variationsModal_regenerate')}
                </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OutfitVariationsModal;
